/**
 * Drives the variant rail on a product card: the row of option-value chips
 * under the price. Clicking one resolves the matching variant and swaps the
 * card's image, price, links, sold-out state and add-to-cart in place, so a
 * shopper can compare colourways without leaving the grid.
 *
 * Markup contract, from `snippets/product-card.liquid`:
 *   <product-card-variant-selector>
 *     <div data-rail data-option-position="1">
 *       <button data-option-value="Brown" aria-pressed="true">…</button>
 *     </div>
 *     <script type="application/json" class="product-card-variants">[…]</script>
 *   </product-card-variant-selector>
 *
 * A custom element rather than a delegated listener because `assets/filters.js`
 * replaces `#ProductList.innerHTML` on every filter, sort and page change, and
 * custom elements re-upgrade on insertion.
 */
class ProductCardVariantSelector extends HTMLElement {
  constructor() {
    super();
    this.handleChipClick = this.onChipClick.bind(this);
  }

  connectedCallback() {
    const jsonEl = this.querySelector(".product-card-variants");
    if (!jsonEl) return;

    try {
      this.variantData = JSON.parse(jsonEl.textContent);
    } catch (e) {
      console.error("ProductCardVariantSelector: Failed to parse variant JSON", e);
      return;
    }

    this.card = this.closest(".product-card");
    if (!this.card) return;

    this.rail = this.querySelector("[data-rail]");
    if (!this.rail) return;

    this.optionPosition = parseInt(this.rail.dataset.optionPosition, 10);
    this.chips = this.rail.querySelectorAll("[data-option-value]");

    /**
     * Deliberately NOT seeded from the chip that renders as pressed. At rest
     * the card shows the product's own featured image, which is usually a
     * lifestyle shot rather than that variant's photo, so the first click on
     * the already-pressed chip still has real work to do.
     */
    this.selectedValue = null;

    this.chips.forEach((chip) => {
      chip.addEventListener("click", this.handleChipClick);
    });
  }

  disconnectedCallback() {
    if (!this.chips) return;
    this.chips.forEach((chip) => {
      chip.removeEventListener("click", this.handleChipClick);
    });
  }

  /**
   * Find the best variant for an option value. Prefers an available one, so a
   * product whose first variant for a colour is sold out still lands on a
   * buyable size; falls back to an unavailable variant so the card can show
   * the sold-out state rather than silently ignoring the click.
   */
  findVariant(value) {
    if (!this.variantData || !this.optionPosition) return null;

    const key = "option" + this.optionPosition;
    const matches = this.variantData.filter((v) => v[key] === value);

    return matches.find((v) => v.available) || matches[0] || null;
  }

  onChipClick(e) {
    e.preventDefault();
    e.stopPropagation();
    this.select(e.currentTarget);
  }

  /**
   * Apply the chip's variant to the card. Idempotent, so re-clicking the
   * chip that is already showing costs nothing.
   */
  select(chip) {
    const value = chip.dataset.optionValue;
    if (value === this.selectedValue) return;

    const variant = this.findVariant(value);
    if (!variant) return;

    this.selectedValue = value;
    this.chips.forEach((other) => {
      other.setAttribute("aria-pressed", String(other === chip));
    });

    /**
     * The card's hover image is the second gallery photo, absolutely positioned
     * over the first and revealed on card hover. The shopper is hovering the
     * card whenever they reach the rail, so that photo covered whatever the
     * swatch swapped in and the change only appeared once the cursor left the
     * card. Retire it for the rest of this card's life — having chosen a
     * colourway, the shopper should keep seeing it.
     */
    this.card.classList.add("product-card--variant-picked");

    this.updateCard(variant);
  }

  /**
   * Apply the selected variant to every part of the card that names it.
   */
  updateCard(variant) {
    const card = this.card;

    // 1. The variant the card's form would add to cart
    const variantInput = card.querySelector('input[name="id"]');
    if (variantInput) variantInput.value = variant.id;

    // 2. Both links to the product
    const imageLink = card.querySelector(".product-card__image-link");
    const titleLink = card.querySelector(".product-card__title-link");
    if (imageLink) {
      const variantUrl = imageLink.dataset.baseUrl + "?variant=" + variant.id;
      imageLink.href = variantUrl;
      if (titleLink) titleLink.href = variantUrl;
    }

    // 3. The card image
    if (variant.featured_image) {
      const img = card.querySelector(".product-image-container img.responsive-image");
      if (img) {
        img.src = variant.featured_image.src;
        img.setAttribute("data-src", variant.featured_image.data_src);
        img.setAttribute("data-srcset", variant.featured_image.srcset);
        img.setAttribute("alt", variant.featured_image.alt);
        img.setAttribute("width", variant.featured_image.width);
        img.setAttribute("height", variant.featured_image.height);
        // Re-trigger lazysizes
        img.classList.remove("lazyloaded");
        img.classList.add("lazyload");
      }
    }

    // 4. Sold-out overlay and badge
    const overlay = card.querySelector("[data-sold-out-overlay]");
    const badge = card.querySelector("[data-sold-out-badge]");
    if (overlay) overlay.style.display = variant.available ? "none" : "";
    if (badge) badge.style.display = variant.available ? "none" : "";

    // 5. Price
    const priceContainer = card.querySelector("[data-price-container]");
    if (priceContainer) {
      let html = '<div class="product-price-container-content">';

      if (variant.compare_at_price_formatted) {
        /**
         * `.product-compare-at-price-wrapper` is what right-aligns the sale
         * price over its struck compare-at. Rebuilding the price without it
         * used to drop the whole block to the left on the first chip click,
         * so the layout moved as soon as anyone touched the card.
         */
        html += '<div class="product-compare-at-price-wrapper">';
        html +=
          '<span class="product-price" style="color: var(--color-price-foreground);">' +
          variant.price_formatted +
          "</span>";
        html += '<s class="compare-at-price">' + variant.compare_at_price_formatted + "</s>";
        html += "</div>";
      } else {
        html += '<span class="product-price">' + variant.price_formatted + "</span>";
      }

      html += "</div>";

      // Only emit the promo chip when there is a discount to name — an empty
      // one still paints its background and padding.
      const showPromo = priceContainer.dataset.showPromo === "true";
      if (showPromo && variant.discount_percentage > 0) {
        html += '<div class="product-discounted-promo"><span>' + variant.discount_percentage + "% off</span></div>";
      }

      priceContainer.innerHTML = html;
    }

    // 6. Add-to-cart
    const atcButton = card.querySelector("add-to-cart button");
    if (atcButton) {
      const atcText = atcButton.querySelector(".atc-button__text");
      atcButton.disabled = !variant.available;
      if (atcText) {
        atcText.textContent = variant.available ? "ADD TO CART" : "SOLD OUT";
      }
    }
  }
}

customElements.define("product-card-variant-selector", ProductCardVariantSelector);
